import {
  type AccordionControlsHelpers,
  AccordionSection,
} from "@polestar/component-warehouse-react";
import { useRef, useEffect } from "react";

import scrollToRef from "../../helpers/scrollToRef";
import useOnSlugMatch from "../../hooks/useOnSlugMatch";

import AccordionPanel from "./AccordionPanel";

import {
  type DocumentAccordionRecord,
  type AccordionPanelTable,
} from "~/types";

interface Props {
  readonly panel: DocumentAccordionRecord["panels"][number];
  readonly controls: AccordionControlsHelpers;
  readonly parentSlug: string;
  readonly initiallyExpandedPanels: Record<string, boolean> | undefined;
  readonly onExpandParent: (expanded: boolean) => void;
}

/**
 * The Legal Accordion Section Wrapper component is used to handle the expansion of an accordion panel
 * and scroll to the panel when it is expanded.
 *
 * *Note:
 * Due to animations during expand/collapse, the scroll is delayed by 500ms to ensure the accordion is fully expanded
 * before attempting to scroll to the ref. This can cause race conditions and any changes therefore need to be tested rigorously.*
 */
const AccordionSectionWrapper = ({
  panel,
  controls,
  parentSlug,
  initiallyExpandedPanels,
  onExpandParent,
}: Props) => {
  const ref = useRef(null);
  const shouldScroll = useRef<boolean>(false);

  useOnSlugMatch(panel.article?.slug ?? undefined, () => {
    controls.expand(`accordion-${parentSlug}`);
    onExpandParent(true);
    if (panel.article?.slug) {
      controls.expand(`accordion-${panel.article?.slug}`);
    }

    shouldScroll.current = true;
  });

  useEffect(() => {
    for (const panel in initiallyExpandedPanels) {
      if (initiallyExpandedPanels[panel] && !controls.isExpanded(panel)) {
        controls.expand(panel);
      }
    }
  }, [controls, initiallyExpandedPanels]);

  return (
    <AccordionSection
      ref={ref}
      key={panel.article?.slug ?? panel.article?.id}
      id={`accordion-${panel.article?.slug}`}
      border={false}
      title={panel.article?.title}
      onToggle={(id) => {
        controls.toggle(id);
      }}
      onToggleEnded={(id) => {
        if (controls.isExpanded(id) && shouldScroll.current) {
          // Delay the scroll to ref to ensure the accordion is fully expanded
          setTimeout(() => {
            scrollToRef(ref, 80);
            shouldScroll.current = false;
          }, 500);
        }
      }}
    >
      <AccordionPanel
        body={panel.body ?? undefined}
        table={panel.table as AccordionPanelTable}
        article={panel.article}
      />
    </AccordionSection>
  );
};

export default AccordionSectionWrapper;
